import { login, sendLoginCode, verifyLoginCode } from './api';
import { showLoader, hideLoader } from '../../modules/loaders';
import { keepCodeDigits } from './codeField';

/**
 * Getting somebody into the account they already have.
 *
 * A password and an emailed code are the same episode with two credentials, which
 * is why they are one module: the request differs by one key and one endpoint, and
 * everything after the answer arrives is identical. Splitting them means writing
 * that answer reading twice, and the two copies drift.
 *
 * The reading is here; the reacting is the door's. `onVerdict` gets one of four
 * words and the door decides what the person sees, because "rejected" on a chapter
 * is not the sentence the plans door would write.
 *
 * The profile travels with the credential on purpose: signing in to join something
 * is also the moment the profile is filled in, and the server writes it through
 * UserProfileData. A door that is only signing somebody in supplies nothing.
 */

const VERDICT = {
    signedIn: 'signed_in',
    rejected: 'rejected',
    subscriptionRequired: 'subscription_required',
    unverified: 'unverified'
};

const REFUSED = {
    password: 'password',
    code: 'code',
    answer: 'answer'
};

function approvedFlag(response) {
    if (! response) {
        return false;
    }

    if (typeof response.status !== 'undefined') {
        return response.status === 'approved';
    }

    if (typeof response.is_user_approved !== 'undefined') {
        return Boolean(response.is_user_approved);
    }

    return false;
}

/**
 * Signing in regenerates the session, and the token sitting in the page's meta tag
 * stops matching. Anything the page posts afterwards is refused, and the exception
 * handler answers a token mismatch with a redirect to /login rather than a 419 —
 * which arrives as a page of HTML and reads as a wrong code.
 */
function refreshCsrfToken(data) {
    if (! data || ! data.csrf_token) {
        return;
    }

    $('meta[name="csrf-token"]').attr('content', data.csrf_token);
    $('input[name="_token"]').val(data.csrf_token);
}

/**
 * What the server just said about this person's standing, in one word.
 *
 * Exported because the same answer shape comes back from two different posts: a
 * sign-in, and a join sent by somebody already signed in. Both have to tell a
 * refusal from a plan wall from a seat that is waiting on a chair.
 */
export function readVerdict(response) {
    if (response.status === 'rejected') {
        return response.rejection_reason === 'subscription_required'
            ? { verdict: VERDICT.subscriptionRequired, detail: { upgradeUrl: response.upgrade_url } }
            : { verdict: VERDICT.rejected, detail: { response } };
    }

    if (response.status === 'pending_verification') {
        return { verdict: VERDICT.unverified, detail: { userId: response.user_id } };
    }

    return { verdict: VERDICT.signedIn, detail: { approved: approvedFlag(response), response } };
}

export function signIn({ emailField, profile, translate, onVerdict, onRefused, onSettled } = {}) {
    const say = translate || ((key, fallback) => fallback);

    // Which box holds the address, when the page has more than one. The speaker page
    // shows a sign-in form and a registration form together, so "the email field" is
    // ambiguous there and the flow has to say which.
    const address = emailField || '#email, #login_email';

    keepCodeDigits();

    function verdict(name, detail) {
        if (onVerdict) {
            onVerdict(name, detail || {});
        }
    }

    function refused(kind, message) {
        if (onRefused) {
            onRefused(kind, message);
        }
    }

    function email() {
        return $(address).first().val() || '';
    }

    // `kind` is passed because what travels with a credential is not always the same
    // for both: the speaker page marks the code path a simple_login and the password
    // path not, which changes which checks the controller runs.
    function payload(credential, kind) {
        return Object.assign({ email: email() }, profile ? profile(kind) : {}, credential);
    }

    function read(response) {
        if (! response || ! response.success) {
            refused(REFUSED.answer, (response && response.message)
                || say('invalid_login', 'Invalid login. Please try again.'));
            return;
        }

        refreshCsrfToken(response);

        const answer = readVerdict(response);

        // The address is only known on this side: the server was asked about a
        // person, not told which box the page has it in.
        if (answer.verdict === VERDICT.unverified) {
            answer.detail.email = email();
        }

        verdict(answer.verdict, answer.detail);
    }

    // The sign-in on its way, if any. A second click before it answers posts the
    // credential again with the session the first one replaces, and that answer
    // signs the person out.
    let pending = null;

    function attempt(send, refusalKind, refusalMessage) {
        if (pending) {
            return pending;
        }

        showLoader();

        pending = send()
            .done(read)
            .fail(() => refused(refusalKind, refusalMessage))
            .always(() => {
                hideLoader();
                pending = null;

                if (onSettled) {
                    onSettled();
                }
            });

        return pending;
    }

    return {
        withPassword(password) {
            const value = typeof password === 'undefined' ? $('#password').val() : password;

            return attempt(
                () => login(payload({ password: value }, REFUSED.password)),
                REFUSED.password,
                say('invalid_password', 'Invalid password.')
            );
        },

        withCode(code) {
            const value = typeof code === 'undefined'
                ? ($('#login_code_register').val() || $('#login_code_login').val())
                : code;

            return attempt(
                () => verifyLoginCode(payload({ login_code: value }, REFUSED.code)),
                REFUSED.code,
                say('wrong_code_text', 'The code you entered is incorrect.')
            );
        },

        /** Asking for the code. Whether it arrived is all the caller learns. */
        requestCode() {
            return sendLoginCode(email());
        }
    };
}

export { VERDICT, REFUSED };
